/**
 * OS ITENS DA ASSINATURA NO EDITOR: quando agrupar, e o papel de cada item.
 *
 * ⛔ Por quê (02/10/2026): a assinatura da FESTVAL tem 78 itens numa lista
 * plana, com o local num dropdown dentro de cada linha. Para mexer no ramal da
 * loja L13 era preciso rolar os 78 procurando qual tinha L13 selecionado. E não
 * havia onde conferir se matriz + lojas somava o MRR da assinatura.
 *
 * ⛔ E o primeiro desenho errou em dois pontos (corrigidos em 02/10/2026):
 *
 * 1. AGRUPAVA TODO MUNDO. Das 142 assinaturas ativas, 27 têm 1 item, 97 têm de
 *    2 a 5, 17 têm de 6 a 10 e UMA tem 78; só uma usa locationId (FESTVAL). O
 *    agrupamento, que serve a uma, aparecia nas 142: a Conceito, com 6 itens que
 *    cabiam numa tela, ganhou dois cabeçalhos e um clique a mais. Agora só agrupa
 *    quando se paga: `deveAgrupar`.
 *
 * 2. O RÓTULO MENTIA. A regra era `é ramal ? ramais : infraestrutura`, e tudo que
 *    não era ramal virava "infraestrutura compartilhada", inclusive DDR (consumo
 *    próprio) e os descontos. Na Conceito a tela dizia infra 396,71 onde o certo
 *    é rateado 480,84, próprio 415,80 e descontos −127,93. Agora cada item tem um
 *    PAPEL (`papelDoItem`), e é ele que decide.
 */

export interface ItemAgrupavel {
  productId?: number | null;
  productCode?: string | null;
  name?: string | null;
  locationId?: number | null;
  totalValue?: number | string | null;
}

export interface LocalAgrupavel {
  id: number;
  code?: string | null;
  name?: string | null;
}

export type TipoDoGrupoDeItens = 'novos' | 'matriz' | 'local' | 'local_desconhecido';

/** As listas DENTRO do grupo da matriz. */
export type TipoDaListaDaMatriz = 'compartilhado' | 'proprio' | 'descontos';

export interface ListaDaMatriz {
  tipo: TipoDaListaDaMatriz;
  rotulo: string;
  indices: number[];
  subtotal: number;
}

export interface GrupoDeItens {
  chave: string;
  tipo: TipoDoGrupoDeItens;
  rotulo: string;
  /** Índices em `items` (o editor altera por índice). */
  indices: number[];
  subtotal: number;
  /** Só no grupo da matriz: o que ela compartilha, o que é dela e os descontos. */
  listas?: ListaDaMatriz[];
}

const num = (v: unknown) => {
  const n = parseFloat(String(v));
  return isFinite(n) ? n : 0;
};

export const ehRamal = (code: unknown) => /^CLOUD-RAMAL/i.test(String(code || ''));

/** Acima disto, a lista simples deixa de caber numa tela. */
export const LIMITE_LISTA_SIMPLES = 10;

/**
 * Agrupar só quando se paga: há item por filial (locationId), ou a assinatura
 * passa de 10 itens. Abaixo disso, lista simples, sem cabeçalho nenhum.
 */
export function deveAgrupar(items: ItemAgrupavel[]): boolean {
  return items.some((i) => !!i.locationId) || items.length > LIMITE_LISTA_SIMPLES;
}

/**
 * O PAPEL do item na conta da empresa.
 *  - `desconto`: valor negativo, ou produto de desconto (DESCONTO-*). Testado
 *    PRIMEIRO: na Conceito os descontos também são rateados, e o papel que
 *    importa para quem lê é "desconto";
 *  - `rateado`: produto que o RATEIO DO GRUPO lista (`produtoIds` da API
 *    `/clients/:id/rateio`). ⛔ Não se lê mais o nome do item: até 02/10/2026 o
 *    sinal era o texto "(rateio 68,89%)" no nome, provisório; o rateio virou dado
 *    no backend (#281/#282). Sem a lista (cliente sem rateio, ou ainda
 *    carregando), nada é rateado;
 *  - `proprio`: o resto, consumo da própria empresa (ramais, DDR).
 */
export type PapelDoItem = 'rateado' | 'proprio' | 'desconto';

export type ProdutosRateados = ReadonlySet<number> | null | undefined;

export function papelDoItem(item: ItemAgrupavel, rateados?: ProdutosRateados): PapelDoItem {
  const code = String(item.productCode || '');
  if (num(item.totalValue) < 0 || /^DESCONTO/i.test(code)) return 'desconto';
  if (rateados && item.productId != null && rateados.has(Number(item.productId))) return 'rateado';
  return 'proprio';
}

export interface ResumoPorPapel {
  rateado: number;
  proprio: number;
  desconto: number;
  /** Há algum item rateado ou desconto (só aí o resumo diz algo além do total). */
  temPapelEspecial: boolean;
}

export function resumoPorPapel<T extends ItemAgrupavel>(items: T[], incluir: (item: T, idx: number) => boolean = () => true, rateados?: ProdutosRateados): ResumoPorPapel {
  const r = { rateado: 0, proprio: 0, desconto: 0 };
  items.forEach((item, idx) => {
    if (!incluir(item, idx) || !item.productId) return;
    r[papelDoItem(item, rateados)] += num(item.totalValue);
  });
  return { ...r, temPapelEspecial: r.rateado !== 0 || r.desconto !== 0 };
}

/** DDR é número da própria empresa: consumo próprio, nunca infraestrutura. */
export const ehDdr = (code: unknown) => /DDR/i.test(String(code || ''));

/**
 * Agrupa os índices de `items` que passam em `incluir` (no editor: os MRR e os
 * ainda sem produto). Grupos vazios não aparecem. Chamar só quando
 * `deveAgrupar` disser que sim.
 *
 * A MATRIZ é um grupo no mesmo molde das filiais ("Matriz · FESTVAL", com
 * subtotal), e por dentro se divide em listas por PAPEL (pedido do Heitor,
 * 02/10/2026, sobre a prévia):
 *  - Compartilhado: o rateado e, numa assinatura COM filiais, a infraestrutura
 *    que serve as lojas (licença de sistema, franquia, tronco);
 *  - Próprio: ramais e DDR da matriz. ⛔ DDR é número da própria empresa, não
 *    infraestrutura: na primeira versão os 12 DDR da FESTVAL caíam em
 *    "compartilhado". Medido em produção: compartilhado 1.356,90 (licença 219,90 +
 *    franquia 30 canais 1.137,00) e próprio 10.952,90 (ramais 10.690,10 + 12 DDR
 *    262,80);
 *  - Descontos.
 */
export function agruparItensPorLocal<T extends ItemAgrupavel>(
  items: T[],
  locations: LocalAgrupavel[],
  incluir: (item: T, idx: number) => boolean,
  nomeDaMatriz?: string | null,
  rateados?: ProdutosRateados,
): GrupoDeItens[] {
  const comFiliais = items.some((i) => !!i.locationId);
  const novos: number[] = [];
  const compartilhado: number[] = [];
  const proprio: number[] = [];
  const descontos: number[] = [];
  const porLocal = new Map<number, number[]>();
  const desconhecidos: number[] = [];
  const idsConhecidos = new Set(locations.map((l) => l.id));

  items.forEach((item, idx) => {
    if (!incluir(item, idx)) return;
    if (!item.productId) { novos.push(idx); return; }
    const loc = item.locationId ? Number(item.locationId) : null;
    if (loc == null) {
      const papel = papelDoItem(item, rateados);
      if (papel === 'desconto') descontos.push(idx);
      else if (papel === 'rateado') compartilhado.push(idx);
      else if (ehRamal(item.productCode) || ehDdr(item.productCode)) proprio.push(idx);
      else (comFiliais ? compartilhado : proprio).push(idx);
      return;
    }
    if (!idsConhecidos.has(loc)) { desconhecidos.push(idx); return; }
    const arr = porLocal.get(loc) || [];
    arr.push(idx);
    porLocal.set(loc, arr);
  });

  const soma = (ids: number[]) => ids.reduce((s, i) => s + num(items[i].totalValue), 0);
  const grupos: GrupoDeItens[] = [];
  const push = (chave: string, tipo: TipoDoGrupoDeItens, rotulo: string, indices: number[], listas?: ListaDaMatriz[]) => {
    if (indices.length > 0) grupos.push({ chave, tipo, rotulo, indices, subtotal: soma(indices), ...(listas ? { listas } : {}) });
  };

  push('novos', 'novos', 'Itens novos (escolha o produto)', novos);
  const listas: ListaDaMatriz[] = ([
    ['compartilhado', 'Compartilhado', compartilhado],
    ['proprio', 'Próprio', proprio],
    ['descontos', 'Descontos', descontos],
  ] as Array<[TipoDaListaDaMatriz, string, number[]]>)
    .filter(([, , ids]) => ids.length > 0)
    .map(([tipo, rotulo, ids]) => ({ tipo, rotulo, indices: ids, subtotal: soma(ids) }));
  push('matriz', 'matriz', nomeDaMatriz ? `Matriz · ${nomeDaMatriz}` : 'Matriz', [...compartilhado, ...proprio, ...descontos], listas);
  for (const l of locations) {
    const ids = porLocal.get(l.id);
    if (ids) push(`l${l.id}`, 'local', [l.code, l.name].filter(Boolean).join(' · ') || `Local #${l.id}`, ids);
  }
  push('desconhecido', 'local_desconhecido', 'Local que não existe mais no cadastro', desconhecidos);
  return grupos;
}
